/**
 * Client-side identity carried on every poll request, so the NEXT flood names
 * its own cause instead of leaving us to infer it (#843).
 *
 * ## Why this exists
 *
 * The 2026-08-17 export proved what was happening — one browser, ~170 req/s,
 * 5,000 requests, every error EMAXCONN — and could not prove WHY. The bell's
 * cadence logic was correct and the job it was waiting on was 18 seconds old,
 * so the rate could only come from many poll loops running at once. But the log
 * had no client identity in it at all, which left three candidates
 * indistinguishable:
 *
 *   1. many TABS, each behaving perfectly;
 *   2. one tab with many CHAINS, from repeated component remounts;
 *   3. one chain looping without honouring its own delay.
 *
 * Those need different fixes, and guessing between them is how this class of
 * bug survived six tickets. Three fields settle it on sight.
 *
 * ## Why the query string, specifically
 *
 * Because that is what the log actually records. The Vercel export has a
 * `requestQueryString` column, so these values land in the same CSV that was
 * used to diagnose the incident, for real users, with no extra tooling and no
 * reliance on someone reproducing it with a console open. Request headers are
 * not in that export; `console.log` on a user's machine is not anywhere.
 *
 * Deliberately NOT personal data: a random per-tab id that dies with the tab.
 * It identifies a browser tab within one session, never a person or a device.
 */

const TAB_ID_KEY = 'cheffo:poll-tab-id';

/**
 * Stable for the life of ONE tab, and different in every other tab.
 *
 * `sessionStorage` rather than a module variable, because a module variable
 * dies with the JS context and would give a fresh id after every remount —
 * which would make cases 1 and 2 above look identical, the exact confusion this
 * exists to end. It is per-tab by definition (a duplicated tab gets its own),
 * which is precisely the grain needed.
 */
function getTabId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    let id = window.sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 10);
      window.sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled. A per-context id is still better than
    // none — it just cannot outlive a remount.
    return 'nostore';
  }
}

/** How many poll chains this tab has STARTED, ever. */
let chainsStarted = 0;

/** Call once per poll chain (i.e. per mount that begins polling). */
export function nextPollChainId(): string {
  chainsStarted += 1;
  return `${chainsStarted}`;
}

/** Chains started so far in this tab — the mount-churn signal. */
export function pollChainsStarted(): number {
  return chainsStarted;
}

/**
 * Query string appended to a poll request, e.g.
 * `?t=k3f9a1x2&c=1&n=7&f=0&s=1`
 *
 * Read it in the log like this:
 *
 *   - many distinct `t`      → many tabs. The app is fine; something is opening
 *                              tabs, or the household genuinely has that many.
 *   - one `t`, many `c`      → mount churn. One tab, a new chain per remount,
 *                              each with its backoff reset — the #843 theory.
 *   - one `t`, one `c`,
 *     `n` climbing fast      → a single chain ignoring its own delay: a true
 *                              runaway loop, and a different bug from either.
 *   - `f` stuck at 0 while
 *     the rate is high       → backoff is being RESET rather than failing to
 *                              work, which points at churn again.
 */
export function pollDiagnosticParams(chainId: string, tickSeq: number, consecutiveFailures: number): string {
  const params = new URLSearchParams({
    t: getTabId(),
    c: chainId,
    n: String(tickSeq),
    f: String(consecutiveFailures),
    s: String(chainsStarted),
  });
  return params.toString();
}
